/**
 * Landing Page — Iter-108
 *
 * Public-facing marketing page. Sells the category:
 *   "Audit-grade AI Decision ERP for Contracting"
 *
 * Primary CTA: "Try Live Demo" → auto-logs in as demo user → /ai-insights
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Brain, ShieldCheck, GitBranch, Wallet, Activity, Briefcase,
  Sparkles, Check, ArrowLeft, Play, Lock, ChevronDown,
  Building2, Eye, Layers, Zap, Mail, X, HardHat, Wrench, Settings2, ClipboardList,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const BRAINS = [
  { icon: GitBranch,  color: "indigo",  title: "Procurement Brain",
    desc: "RFQ Intelligence · Vendor Quotation analysis · Item resolver · Award flows" },
  { icon: Wallet,     color: "emerald", title: "Financial Brain",
    desc: "Customer exposure · DSO intelligence · Multi-factor risk scoring · Collection workspace" },
  { icon: ShieldCheck, color: "violet",  title: "Audit Brain",
    desc: "Event-driven trail · Big4-ready PDF + ZIP bundles · VAT period locks · Tamper-evident" },
  { icon: Briefcase,  color: "amber",   title: "Executive Brain",
    desc: "CFO Monthly Pack · Live P&L per project · Compliance snapshot · Board-ready" },
  { icon: Brain,      color: "rose",    title: "AI Decision Layer",
    desc: "Late-payment prediction · Cashflow forecast · Vendor savings · Every score explainable" },
  { icon: Activity,   color: "sky",     title: "Contract Execution",
    desc: "Site Instructions → Variation Orders → Auto Amendments · Full ripple integration" },
];

const DIFFERENTIATORS = [
  { icon: Eye,    title: "Audit-grade AI",
    desc: "Every AI prediction has an explanation drawer with full factor breakdown. Built for Big4 auditors, not for black-box trust." },
  { icon: Zap,    title: "Event-driven Financial Engine",
    desc: "Business Action → Event → Policy → Journal. No hardcoded postings. Full SOX-like traceability out of the box." },
  { icon: Layers, title: "Multi-tenant SaaS",
    desc: "Strict tenant isolation. Per-tenant counters. Per-tenant credit limits. Per-tenant audit trails. Built for partners and franchise expansion." },
];

const AUDIENCES = [
  { icon: HardHat,      title: "المقاولون الرئيسيون",
    desc: "إدارة العقود، VO/PECE، Progress Claims، حماية هامش الربح في كل مشروع." },
  { icon: Wrench,       title: "شركات MEP",
    desc: "تتبّع الـ Subcontracts، استلام المواد، Material Issues، والربط مع Main Contractor." },
  { icon: Settings2,    title: "التشغيل والصيانة (O&M)",
    desc: "أوامر العمل المتكررة، أصول الـ Fleet، عقود الصيانة، وإدارة الأطقم الميدانية." },
  { icon: ClipboardList, title: "إدارة المشاريع (PMC)",
    desc: "مراقبة المقاولين، اعتمادات الـ VO، تقارير مالية مستقلة، وحوكمة كاملة." },
];

const PRICING_TIERS = [
  { name: "Starter",      price: "SAR 1,500",  unit: "/mo",
    features: ["Up to 5 users", "Single tenant", "Procurement + Finance",
               "Email support"] },
  { name: "Professional", price: "SAR 4,900",  unit: "/mo",   popular: true,
    features: ["Up to 25 users", "Multi-tenant (3)", "All 6 brains",
               "AI Decision Cockpit", "CFO Monthly Pack",
               "Audit Packaging PDF + ZIP", "Priority support"] },
  { name: "Enterprise",   price: "Custom",      unit: "",
    features: ["Unlimited users", "Unlimited tenants", "Dedicated SLA",
               "On-prem option", "Custom integrations",
               "ZATCA + Big4 certification"] },
];

const HeroBackground = () => (
  <div className="absolute inset-0 overflow-hidden -z-10">
    <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-violet-950 to-slate-900" />
    <div className="absolute top-1/4 left-1/4 h-96 w-96 rounded-full bg-violet-500/20 blur-3xl" />
    <div className="absolute bottom-1/4 right-1/4 h-96 w-96 rounded-full bg-emerald-500/10 blur-3xl" />
    <div className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.3) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }} />
  </div>
);

export default function Landing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [leadForm, setLeadForm] = useState({
    name: "", email: "", phone: "", company: "", company_type: "main_contractor", message: "",
  });
  const [leadSubmitting, setLeadSubmitting] = useState(false);

  const tryDemo = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/auth/login`, {
        email: "demo@al-bunyan.com",
        password: "Demo@2026!",
      }, { timeout: 30000 });
      const token = r.data?.token || r.data?.access_token;
      if (!token) throw new Error("no token");
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(r.data.user || {}));
      toast.success("مرحباً بك في Al-Bunyan Contracting (Demo)");
      // Land on the most impressive screen — the AI Cockpit.
      window.location.href = "/ai-insights";
    } catch (e) {
      toast.error("لم نتمكن من فتح الـ Demo — تواصل معنا للحصول على وصول");
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e?.preventDefault();
    if (!leadForm.name.trim() || !leadForm.email.trim()) {
      toast.error("الاسم والبريد الإلكتروني مطلوبان");
      return;
    }
    setLeadSubmitting(true);
    try {
      await axios.post(`${API}/api/leads/request-demo`, leadForm, { timeout: 20000 });
      toast.success("تم استلام طلبك — سنتواصل معك خلال 24 ساعة");
      setShowLead(false);
      setLeadForm({ name: "", email: "", phone: "", company: "", company_type: "main_contractor", message: "" });
    } catch (err) {
      toast.error("تعذّر إرسال الطلب — جرّب مجدداً أو افتح الـ Demo مباشرة");
    } finally {
      setLeadSubmitting(false);
    }
  };

  return (
    <div data-testid="landing-page" className="min-h-screen bg-slate-950 text-slate-100" dir="rtl">
      {/* Nav */}
      <header className="relative z-10 flex items-center justify-between px-6 py-5 md:px-12">
        <div className="flex items-center gap-2 text-xl font-bold">
          <Brain className="h-6 w-6 text-violet-400" />
          <span>DISTINCT HIGH<span className="text-violet-400"> </span>ROAD</span>
        </div>
        <nav className="hidden md:flex items-center gap-8 text-sm text-slate-300">
          <a href="#brains"        className="hover:text-white">المحركات</a>
          <a href="#audiences"      className="hover:text-white">لمن؟</a>
          <a href="#differentiators" className="hover:text-white">لماذا نحن؟</a>
          <a href="#pricing"        className="hover:text-white">الأسعار</a>
          <a href="#contact"        className="hover:text-white">تواصل</a>
        </nav>
        <div className="flex items-center gap-2">
          <button
            data-testid="landing-login"
            onClick={() => navigate("/login")}
            className="rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 hover:border-violet-500 hover:text-white"
          >
            تسجيل دخول
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden px-6 pt-12 pb-24 md:px-12 md:pt-20">
        <HeroBackground />
        <div className="mx-auto max-w-5xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-violet-500/30 bg-violet-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-violet-300">
            <Sparkles className="h-3.5 w-3.5" />
            فئة جديدة في السوق · 2026
          </span>
          <h1 className="mt-6 text-4xl font-bold leading-tight md:text-6xl">
            <span className="bg-gradient-to-r from-violet-300 via-violet-100 to-emerald-300 bg-clip-text text-transparent">
              ERP للمقاولات بذكاء قابل للتدقيق
            </span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base text-slate-300 md:text-lg leading-relaxed">
            ليس ERP عادي. ليس BI tool. ليس FP&amp;A app.<br />
            <span className="text-violet-300 font-semibold">
              منصة قرار ذكية
            </span>{" "}
            تربط الشراء + المالية + التدقيق + AI في نظام واحد —
            كل توقع شفاف، كل عملية مُدقَّقة، كل قرار مدعوم بالبيانات.
          </p>

          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              data-testid="landing-try-demo"
              onClick={tryDemo}
              disabled={loading}
              className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-500 to-violet-700 px-7 py-3.5 text-base font-bold text-white shadow-lg shadow-violet-500/30 transition-all hover:scale-105 hover:shadow-violet-500/50 disabled:opacity-60"
            >
              <Play className="h-4 w-4" />
              {loading ? "جارٍ تحضير الـ Demo…" : "جرّب الـ Demo المباشر"}
              <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            </button>
            <button
              data-testid="landing-request-demo"
              onClick={() => setShowLead(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-violet-500/40 bg-violet-500/10 px-7 py-3.5 text-base font-semibold text-violet-200 backdrop-blur transition-all hover:border-violet-400 hover:bg-violet-500/20 hover:text-white"
            >
              <Mail className="h-4 w-4" />
              اطلب Demo شخصي
            </button>
            <button
              onClick={() => document.getElementById("brains")?.scrollIntoView({behavior:"smooth"})}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/50 px-7 py-3.5 text-base font-medium text-slate-200 backdrop-blur hover:border-slate-500 hover:text-white"
            >
              شاهد كيف يعمل
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-8 flex items-center justify-center gap-6 text-xs text-slate-400">
            <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400" /> 10 دقائق demo</span>
            <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400" /> بدون بطاقة ائتمان</span>
            <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400" /> ZATCA + Big4 ready</span>
          </div>
        </div>

        {/* Live KPI strip — implies real data */}
        <div className="mx-auto mt-16 grid max-w-4xl gap-3 sm:grid-cols-3" data-testid="landing-kpi-strip">
          {[
            {v: "98.5/100",  l: "تقييم معماري",    c: "violet"},
            {v: "6 brains",  l: "أنظمة ذكاء",      c: "emerald"},
            {v: "60 sec",    l: "للقرار التنفيذي", c: "amber"},
          ].map((k, i) => (
            <div key={i} className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5 text-center backdrop-blur">
              <div className={`text-3xl font-bold text-${k.c}-400`}>{k.v}</div>
              <div className="mt-1 text-xs uppercase tracking-wider text-slate-400">{k.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Brains */}
      <section id="brains" className="px-6 py-20 md:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white md:text-4xl">
              6 محركات ذكاء في نظام واحد
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-400">
              ليست modules — بل أدمغة متخصصة تتبادل البيانات في الزمن الحقيقي.
              كل قرار في أي محرك يُسجَّل تلقائياً في الـ Audit Trail.
            </p>
          </div>
          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="landing-brains-grid">
            {BRAINS.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className={`group rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-900/50 p-6 transition-all hover:border-${b.color}-500/50 hover:-translate-y-1`}>
                  <div className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-${b.color}-500/15 text-${b.color}-400`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className={`text-lg font-bold text-${b.color}-300`}>{b.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Audiences — Who is this for? */}
      <section id="audiences" className="border-t border-slate-800 bg-slate-900/30 px-6 py-20 md:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white md:text-4xl">لمن صُمِّم هذا النظام؟</h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-400">
              مبني خصيصاً لقطاع المقاولات في الخليج — وليس ERP عام مُكيَّف.
            </p>
          </div>
          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="landing-audiences-grid">
            {AUDIENCES.map((a, i) => {
              const Icon = a.icon;
              return (
                <div key={i} className="group rounded-2xl border border-slate-800 bg-slate-900/60 p-6 transition-all hover:border-violet-500/40 hover:-translate-y-1">
                  <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-violet-500/15 text-violet-300">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-base font-bold text-white">{a.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{a.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section id="differentiators" className="border-y border-slate-800 bg-gradient-to-b from-slate-950 to-slate-900/50 px-6 py-20 md:px-12">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white md:text-4xl">
              لماذا نحن مختلفون فعلاً؟
            </h2>
            <p className="mt-3 text-sm text-slate-400">
              ثلاث ركائز معمارية لا تجدها في SAP أو Oracle أو Procore
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {DIFFERENTIATORS.map((d, i) => {
              const Icon = d.icon;
              return (
                <div key={i} className="rounded-2xl border border-violet-500/20 bg-violet-500/5 p-6">
                  <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-violet-500/20 text-violet-300">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold text-white">{d.title}</h3>
                  <p className="mt-2 text-sm text-slate-300 leading-relaxed">{d.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="px-6 py-20 md:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white md:text-4xl">أسعار شفافة</h2>
            <p className="mt-3 text-sm text-slate-400">ابدأ بسعر صغير. تنمو معك.</p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-3" data-testid="landing-pricing">
            {PRICING_TIERS.map((t, i) => (
              <div key={i}
                    className={`relative rounded-2xl border ${t.popular ? "border-violet-500 bg-gradient-to-br from-violet-900/30 to-slate-900" : "border-slate-800 bg-slate-900/50"} p-6`}>
                {t.popular && (
                  <span className="absolute -top-3 right-6 rounded-full bg-violet-500 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                    الأكثر طلباً
                  </span>
                )}
                <h3 className="text-xl font-bold text-white">{t.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-white">{t.price}</span>
                  <span className="text-sm text-slate-400">{t.unit}</span>
                </div>
                <ul className="mt-6 space-y-2.5">
                  {t.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-slate-300">
                      <Check className="h-4 w-4 text-emerald-400 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={t.name === "Enterprise" ? () => document.getElementById("contact").scrollIntoView({behavior:"smooth"}) : tryDemo}
                  className={`mt-6 w-full rounded-lg py-2.5 text-sm font-semibold transition-all ${t.popular ? "bg-violet-500 text-white hover:bg-violet-600" : "border border-slate-700 text-slate-200 hover:border-violet-500"}`}
                >
                  {t.name === "Enterprise" ? "تواصل مع المبيعات" : "جرّب الآن"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="border-t border-slate-800 px-6 py-20 md:px-12">
        <div className="mx-auto max-w-4xl rounded-3xl border border-violet-500/30 bg-gradient-to-br from-violet-900/30 via-slate-900 to-slate-900 p-10 text-center">
          <Building2 className="mx-auto h-10 w-10 text-violet-300" />
          <h2 className="mt-4 text-3xl font-bold text-white">
            جاهز لرؤية مقاولتك بطريقة جديدة؟
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-slate-300">
            افتح Demo حقيقي خلال 10 ثوانٍ. اطّلع على شركة «Al-Bunyan Contracting»
            الافتراضية وكيف يكشف لها النظام عملاء عالي الخطر وفرص توفير 50% على المشتريات.
          </p>
          <button
            onClick={tryDemo}
            disabled={loading}
            className="mt-8 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-500 to-violet-700 px-8 py-3.5 text-base font-bold text-white shadow-lg shadow-violet-500/30 hover:scale-105 disabled:opacity-60"
          >
            <Play className="h-4 w-4" />
            {loading ? "جارٍ التحضير…" : "افتح الـ Demo الآن"}
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="mt-6 flex items-center justify-center gap-6 text-xs text-slate-400">
            <span className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Tenant معزول · لا تلوث للبيانات</span>
            <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400" /> مجاناً للتجربة</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 px-6 py-8 text-center text-xs text-slate-500">
        © 2026 DISTINCT HIGH ROAD · Audit-grade AI ERP for Contracting · صُنع في المملكة العربية السعودية
      </footer>

      {/* Request Demo Modal */}
      {showLead && (
        <div
          data-testid="lead-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm px-4"
          onClick={() => !leadSubmitting && setShowLead(false)}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitLead}
            className="relative w-full max-w-lg rounded-2xl border border-violet-500/30 bg-slate-900 p-6 shadow-2xl shadow-violet-500/20"
          >
            <button
              type="button"
              onClick={() => setShowLead(false)}
              disabled={leadSubmitting}
              data-testid="lead-modal-close"
              className="absolute left-4 top-4 rounded-lg p-1 text-slate-400 hover:bg-slate-800 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
            <h3 className="text-xl font-bold text-white">اطلب Demo شخصي</h3>
            <p className="mt-1 text-xs text-slate-400">
              سنتواصل معك خلال 24 ساعة بعرض مُخصَّص لشركتك.
            </p>

            <div className="mt-5 space-y-3">
              <input
                data-testid="lead-name"
                required
                value={leadForm.name}
                onChange={(e) => setLeadForm((s) => ({ ...s, name: e.target.value }))}
                placeholder="الاسم الكامل *"
                className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:border-violet-500 focus:outline-none"
              />
              <input
                data-testid="lead-email"
                required
                type="email"
                value={leadForm.email}
                onChange={(e) => setLeadForm((s) => ({ ...s, email: e.target.value }))}
                placeholder="البريد الإلكتروني *"
                className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:border-violet-500 focus:outline-none"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  data-testid="lead-phone"
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm((s) => ({ ...s, phone: e.target.value }))}
                  placeholder="رقم الجوال"
                  className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:border-violet-500 focus:outline-none"
                />
                <input
                  data-testid="lead-company"
                  value={leadForm.company}
                  onChange={(e) => setLeadForm((s) => ({ ...s, company: e.target.value }))}
                  placeholder="اسم الشركة"
                  className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:border-violet-500 focus:outline-none"
                />
              </div>
              <select
                data-testid="lead-company-type"
                value={leadForm.company_type}
                onChange={(e) => setLeadForm((s) => ({ ...s, company_type: e.target.value }))}
                className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-white focus:border-violet-500 focus:outline-none"
              >
                <option value="main_contractor">مقاول رئيسي</option>
                <option value="mep">شركة MEP</option>
                <option value="om">تشغيل وصيانة (O&M)</option>
                <option value="pmc">إدارة مشاريع (PMC)</option>
                <option value="other">أخرى</option>
              </select>
              <textarea
                data-testid="lead-message"
                value={leadForm.message}
                onChange={(e) => setLeadForm((s) => ({ ...s, message: e.target.value }))}
                placeholder="حدثنا عن احتياجاتك (اختياري)"
                rows={3}
                className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:border-violet-500 focus:outline-none"
              />
            </div>

            <button
              type="submit"
              data-testid="lead-submit"
              disabled={leadSubmitting}
              className="mt-5 w-full rounded-xl bg-gradient-to-r from-violet-500 to-violet-700 py-3 text-sm font-bold text-white shadow-lg shadow-violet-500/30 transition-all hover:scale-[1.02] disabled:opacity-60"
            >
              {leadSubmitting ? "جارٍ الإرسال…" : "إرسال الطلب"}
            </button>
            <p className="mt-3 text-center text-[11px] text-slate-500">
              أو افتح <button type="button" onClick={() => { setShowLead(false); tryDemo(); }} className="text-violet-300 underline hover:text-violet-200">الـ Demo المباشر</button> الآن بدون انتظار.
            </p>
          </form>
        </div>
      )}
    </div>
  );
}
